import React from 'react';
import { CoherencePanel, CoherencePanelSize } from '@coherence-design-system/controls';
import { useForm, FieldValues } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { CoherencePanelStyles } from '../../../app/common/styles/CommonStyleObjects';
import ActionButtons from '../../common/ActionButtons';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import {
  UPDATE_ENTITY_TERMINATION_OVERVIEW_STATUS_INFO,
  GET_ENTITY_TERMINATION_OVERVIEW,
} from '../../../utils/statApi/EntityTerminationApi';
import EntityTerminationStatusEditForm, {
  IStatusInformation,
  isBlockerAndIntakeNotApplicable,
} from './EntityTerminationStatusEditForm';

interface IEntityTerminationStatusEditPanelProps {
  closePanel: () => void;
  companyCode: string;
  hasPrimaryBlocker?: boolean;
  statusInfo?: IStatusInformation;
}

const EntityTerminationStatusEditPanel = (
  props: IEntityTerminationStatusEditPanelProps,
): JSX.Element => {
  const { closePanel, companyCode, hasPrimaryBlocker, statusInfo } = props;
  const statusForm = useForm<FieldValues>({
    defaultValues: {
      ...statusInfo,
    },
  });
  const { handleSubmit } = statusForm;

  const [updateStatusInformation, { loading, error }] = useMutation(
    UPDATE_ENTITY_TERMINATION_OVERVIEW_STATUS_INFO,
    {
      onCompleted: closePanel,
      refetchQueries: [
        {
          query: GET_ENTITY_TERMINATION_OVERVIEW,
          variables: { companyCode },
        },
      ],
    },
  );

  const handleSaveClick = (formData: IStatusInformation): void => {
    // When Blocker Category and Intake Method are not applicable, null their IDs at submit
    // so stale/legacy values are never persisted regardless of how the form was populated.
    const blockerAndIntakeNotApplicable = isBlockerAndIntakeNotApplicable(
      formData.legalStatus?.fieldName,
      formData.lifecycleStatus?.fieldName,
    );

    updateStatusInformation({
      variables: {
        companyCode,
        updateOverviewStatusInfoDto: {
          entityStatusId: formData.legalStatus?.id ?? null,
          lifecycleStatusId: formData.lifecycleStatus?.id ?? null,
          terminationLifecycleStatusId: formData.terminationLifecycleStatus?.id ?? null,
          legoCategoryId: blockerAndIntakeNotApplicable
            ? null
            : formData.blockerCategory?.id ?? null,
          intakeMethodId: blockerAndIntakeNotApplicable ? null : formData.intakeMethod?.id ?? null,
          anticipatedTerminationFY: formData.anticipatedTerminationFY || null,
          anticipatedTerminationQuarter: formData.anticipatedTerminationQuarter || null,
        },
      },
    });
  };

  return (
    <CoherencePanel
      panelSize={CoherencePanelSize.medium}
      titleText="Edit Status Information"
      isOpen
      onDismiss={closePanel}
      styles={CoherencePanelStyles}
      hasCloseButton
      closeButtonAriaLabel="Close Status Information Edit Panel"
      onRenderFooter={(): JSX.Element => (
        <ActionButtons
          mutationLoading={loading}
          closePanel={closePanel}
          saveLabel="Save Status Information"
          saveTitle="Save Status Information"
          cancelLabel="Cancel"
          cancelTitle="Cancel"
          handleSubmit={handleSubmit(handleSaveClick)}
        />
      )}
    >
      <form>
        {error && <LoadingErrorMessage error={error} loading={loading} />}
        <EntityTerminationStatusEditForm
          form={statusForm}
          statusInfo={statusInfo}
          hasPrimaryBlocker={hasPrimaryBlocker}
        />
      </form>
    </CoherencePanel>
  );
};

export default EntityTerminationStatusEditPanel;
